import crypto, { BinaryLike, CipherKey } from 'crypto';
import { WckAuthCookie } from '@wconcept/wconcept-common/models/display/common';
import { isEmpty } from './baseUtils';
import { uuidv4 } from './commonUtils';
import { sanitizeContent } from './htmlSanitizer';

const isSsr = (): boolean => typeof window === 'undefined';

export const decodeCookie = (cookieKey: string, cookieName: string) => {
  let result;
  if (cookieKey) {
    try {
      // wckBridgeAuth 쿠키 key, iv default로 처리, cookieKey가 wckConnectionAuth 이면 wckConnectionAuth의 key, iv 사용
      let key =
        process.env.NEXT_PUBLIC_WCK_BRIDGE_AUTH_DATA_KEY ||
        (process.env.WCK_BRIDGE_AUTH_DATA_KEY as string);
      let iv =
        process.env.NEXT_PUBLIC_WCK_BRIDGE_AUTH_DATA_IV ||
        (process.env.WCK_BRIDGE_AUTH_DATA_IV as string);

      if (
        cookieName ===
        (process.env.NEXT_PUBLIC_WCK_AUTH_COOKIE_KEY || 'wckDisplaySID')
      ) {
        key =
          process.env.NEXT_PUBLIC_WCK_AUTH_DATA_KEY ||
          (process.env.WCK_AUTH_DATA_KEY as string);
        iv =
          process.env.NEXT_PUBLIC_WCK_AUTH_DATA_IV ||
          (process.env.WCK_AUTH_DATA_IV as string);
      }
      // Base64로 인코딩된 키와 IV를 버퍼로 변환
      const keyBuffer = Buffer.from(key, 'base64');
      const ivBuffer = Buffer.from(iv, 'base64');

      // 복호화 객체 생성
      const decipher = crypto.createDecipheriv(
        'aes-256-cbc',
        keyBuffer as unknown as crypto.CipherKey,
        ivBuffer as unknown as crypto.BinaryLike
      );

      // 패딩 설정
      decipher.setAutoPadding(true);

      // 복호화 실행
      let decrypted = decipher.update(cookieKey, 'base64', 'utf8');
      decrypted += decipher.final('utf8');
      // 결과 처리
      const authData = decodeURIComponent(decrypted);
      result = JSON.parse(authData) as unknown;
    } catch (e) {
      console.log(e);
    }
  }
  return result;
};

// WckAuthData 정보 조회.
export const getCookieWckAuthData = async () => {
  let wckAuthData: WckAuthCookie = {
    userNo: '0',
    userId: '',
    userName: '',
    gender: '',
    grade: '8',
    gradeName: '',
    birth: '',
    mobile: '',
    isCert: false,
    isPasswordChange: false,
    isAuthLogin: false,
    lastLoginDate: '',
    joinDate: '',
    lastOrderDate: '',
    snsType: '',
    loginType: '',
    smsYN: '',
    emailYN: '',
    pushYN: '',
    adYN: '',
    liveAlarmYN: '',
    isEmployee: '',
    cookieCreateDate: '',
    locationAgreementYN: '',
    kakaotalkYN: '',
    cooperationCompanyCode: '',
  };

  const bridgeCookieKey = process.env
    .NEXT_PUBLIC_WCK_BRIDGE_AUTH_COOKIE_KEY as string;
  const cookieKey = process.env.NEXT_PUBLIC_WCK_AUTH_COOKIE_KEY as string;

  let wckAuthCookie = '';
  let cookieName = bridgeCookieKey || cookieKey;

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    if (bridgeCookieKey) {
      wckAuthCookie = cookie.get(bridgeCookieKey)?.value as string;
    }
    if (isEmpty(wckAuthCookie) && cookieKey) {
      cookieName = cookieKey;
      wckAuthCookie = cookie.get(cookieKey)?.value as string;
    }
  } else {
    const { getCookie } = await import('cookies-next');
    if (bridgeCookieKey) {
      wckAuthCookie = getCookie(bridgeCookieKey) as string;
    }
    if (isEmpty(wckAuthCookie) && cookieKey) {
      cookieName = cookieKey;
      wckAuthCookie = getCookie(cookieKey) as string;
    }
  }
  const auth = decodeCookie(wckAuthCookie, cookieName) as WckAuthCookie;
  if (auth) {
    wckAuthData = {
      ...wckAuthData,
      ...auth,
      userNo: auth.userNo ?? '0',
      userId: auth.userId ?? '',
      gradeName: auth.gradeName ?? '',
    };
    if (!isEmpty(wckAuthData.userNo)) {
      if (wckAuthData.grade === '0') {
        wckAuthData.grade = '6';
      }
    } else {
      wckAuthData.grade = '8';
    }
  }

  if (!isEmpty(wckAuthData.userNo) && isEmpty(wckAuthData.kakaotalkYN)) {
    wckAuthData.kakaotalkYN = 'N';
  }

  return wckAuthData;
};

// custno 정보 조회.
export const getCookieCustno = async () => {
  let custno = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    custno = cookie.get('custno')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    custno = getCookie('custno') as string;
  }
  if (isEmpty(custno)) {
    custno = '';
  }
  return custno;
};

// TrackingIssueNo 정보 조회.
export const getCookieTrackingIssueNo = async () => {
  let trackingIssueNo = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    trackingIssueNo = cookie.get('trackingIssueNo')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    trackingIssueNo = getCookie('trackingIssueNo') as string;
  }
  if (isEmpty(trackingIssueNo)) {
    trackingIssueNo = '0';
  }
  return trackingIssueNo;
};

export const setCookieTrackingIssueNo = async (value?: string) => {
  if (isEmpty(value)) {
    return;
  }

  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);
  if (value) {
    if (isSsr()) {
      const { cookies } = await import('next/headers');
      const cookie = await cookies();
      cookie.set('trackingIssueNo', value, {
        path: '/',
        domain: '.wconcept.co.kr',
        expires: date,
      });
    } else {
      const { setCookie } = await import('cookies-next');
      setCookie('trackingIssueNo', value, {
        path: '/',
        domain: '.wconcept.co.kr',
        expires: date,
      });
    }
  }
};

// WckGenderValue Cookie에 세팅.
export const setCookieWckGenderValue = async (genderValue: string) => {
  if (isEmpty(genderValue)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckGenderValue', genderValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckGenderValue', genderValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckWMarketingValue(Women) Cookie에 세팅.
export const setCookieWckFloatingBanner = async (nowDate: string) => {
  if (isEmpty(nowDate)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckFloatingBanner', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckFloatingBanner', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};
// wckWMarketingValue(Women) Cookie에 세팅.
export const setCookieWckFullPagePopup = async (nowDate: string) => {
  if (isEmpty(nowDate)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckFullPagePopup', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckFullPagePopup', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckWMarketingValue(Women) Cookie에 세팅.
export const setCookieWckBottomPopup = async (nowDate: string) => {
  if (isEmpty(nowDate)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckBottomPopup', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckBottomPopup', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckWMarketingValue(Women) Cookie에 세팅.
export const setCookieWckWMarketingValue = async (nowDate: string) => {
  if (isEmpty(nowDate)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckWMarketingValue', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckWMarketingValue', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckMMarketingValue(Men) Cookie에 세팅.
export const setCookieWckMMarketingValue = async (nowDate: string) => {
  if (isEmpty(nowDate)) {
    return;
  }
  // 10000 일 추가됨. (10000 * 24 * 60 * 60 * 1000)
  const date = new Date();
  date.setTime(date.getTime() + 864000000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckMMarketingValue', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckMMarketingValue', nowDate, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// adfrom Cookie에 세팅.
export const setCookieAdfrom = async (adfrom: string) => {
  const adfromValue = adfrom.trim();
  if (isEmpty(adfromValue)) {
    return;
  }
  const date = new Date();
  // 20분 60 * 1000 * 20
  date.setTime(date.getTime() + 60 * 1000 * 20);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('adfrom', adfromValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('adfrom', adfromValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

/** ZVZO 인입 adfrom 값 */
const ZVZO_COOKIE_TTL_MS = 60 * 1000 * 60 * 24;

const ZVZO_UTM_COOKIE_ENTRIES = [
  { paramKey: 'utm_content', cookieKey: 'zvzo_utm_content' },
  { paramKey: 'utm_medium', cookieKey: 'zvzo_utm_medium' },
  { paramKey: 'utm_source', cookieKey: 'zvzo_utm_source' },
] as const;

interface SetCookieZvzoParams {
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
}

export const setCookieZvzo = async ({
  utm_content,
  utm_medium,
  utm_source,
}: SetCookieZvzoParams): Promise<void> => {
  const utmValues = {
    utm_content,
    utm_medium,
    utm_source,
  };

  const date = new Date();
  date.setTime(date.getTime() + ZVZO_COOKIE_TTL_MS);

  const cookieOptions = {
    path: '/',
    domain: '.wconcept.co.kr',
    expires: date,
  };

  const zvzoSid = uuidv4();

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();

    cookie.set('zvzo_sid', zvzoSid, cookieOptions);

    for (const { paramKey, cookieKey } of ZVZO_UTM_COOKIE_ENTRIES) {
      const value = utmValues[paramKey]?.trim();
      if (!isEmpty(value) && value) {
        cookie.set(cookieKey, value, cookieOptions);
      }
    }
  } else {
    const { setCookie } = await import('cookies-next');

    setCookie('zvzo_sid', zvzoSid, cookieOptions);

    for (const { paramKey, cookieKey } of ZVZO_UTM_COOKIE_ENTRIES) {
      const value = utmValues[paramKey]?.trim();
      if (!isEmpty(value) && value) {
        setCookie(cookieKey, value, cookieOptions);
      }
    }
  }
};

// channelId Cookie에 세팅.
export const setCookieChannelId = async (channelId: string) => {
  const channelIdValue = channelId.trim();
  if (isEmpty(channelIdValue)) {
    return;
  }
  const date = new Date();
  // 24시간 60 * 1000 * 60 * 24
  date.setTime(date.getTime() + 60 * 1000 * 60 * 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('channel_id', channelIdValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('channel_id', channelIdValue, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// rwcode Cookie에 세팅.
export const setCookieRwcode = async (rwcode: string) => {
  if (isEmpty(rwcode)) {
    return;
  }
  const date = new Date();
  // 24시간 60 * 1000 * 60 * 24
  date.setTime(date.getTime() + 60 * 1000 * 60 * 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('rwcode', rwcode, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('rwcode', rwcode, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// fcustno Cookie에 세팅.
export const setCookieFcustno = async (fcustno: string) => {
  if (isEmpty(fcustno)) {
    return;
  }
  const date = new Date();
  // 1시간 60 * 1000 * 60
  date.setTime(date.getTime() + 60 * 1000 * 60);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('fcustno', fcustno, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('fcustno', fcustno, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

export const getCookieFcustno = async () => {
  let fcustno = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    fcustno = cookie.get('fcustno')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    fcustno = getCookie('fcustno') as string;
  }
  if (isEmpty(fcustno)) {
    fcustno = '';
  }
  return fcustno;
};

export const getCookieAdfrom = async () => {
  let adfrom = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    adfrom = cookie.get('adfrom')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    adfrom = getCookie('adfrom') as string;
  }
  if (isEmpty(adfrom)) {
    adfrom = '';
  }
  return adfrom;
};

export const getCookieChannelId = async () => {
  let channelId = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    channelId = cookie.get('channel_id')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    channelId = getCookie('channel_id') as string;
  }
  if (isEmpty(channelId)) {
    channelId = '';
  }
  return channelId;
};

// 크리에이터 리워드 관련 쿠키 정보 조회.
export const getCookieRwcode = async () => {
  let rwcode = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    rwcode = cookie.get('rwcode')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    rwcode = getCookie('rwcode') as string;
  }
  if (isEmpty(rwcode)) {
    rwcode = '';
  }
  return rwcode;
};

// PC모드 세팅.
export const setCookieType = async (stype: string) => {
  if (isEmpty(stype)) {
    return;
  }
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('stype', stype, { path: '/', domain: '.wconcept.co.kr' });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('stype', stype, { path: '/', domain: '.wconcept.co.kr' });
  }
};

// 장바구니 카운트 조회.
export const getCookieBasketCount = async () => {
  let BasketCnt = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    BasketCnt = cookie.get('BasketCnt')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    BasketCnt = getCookie('BasketCnt') as string;
  }
  if (isEmpty(BasketCnt)) {
    BasketCnt = '0';
  }
  return BasketCnt;
};

export const getCookieWckFullPagePopup = async () => {
  let wckFullPagePopup = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckFullPagePopup = cookie.get('wckFullPagePopup')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckFullPagePopup = getCookie('wckFullPagePopup') as string;
  }
  if (isEmpty(wckFullPagePopup)) {
    wckFullPagePopup = '0';
  }
  return wckFullPagePopup;
};

export const getCookieWckBottomPopup = async () => {
  let wckBottomPopup = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckBottomPopup = cookie.get('wckBottomPopup')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckBottomPopup = getCookie('wckBottomPopup') as string;
  }
  if (isEmpty(wckBottomPopup)) {
    wckBottomPopup = '0';
  }
  return wckBottomPopup;
};

export const getCookieWckFloatingBanner = async () => {
  let wckFloatingBanner = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckFloatingBanner = cookie.get('wckFloatingBanner')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckFloatingBanner = getCookie('wckFloatingBanner') as string;
  }
  if (isEmpty(wckFloatingBanner)) {
    wckFloatingBanner = '0';
  }
  return wckFloatingBanner;
};

// WckWMarketingValue(Women) 정보 조회.
export const getCookieWckWMarketingValue = async () => {
  let wckWMarketingValue = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckWMarketingValue = cookie.get('wckWMarketingValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckWMarketingValue = getCookie('wckWMarketingValue') as string;
  }
  if (isEmpty(wckWMarketingValue)) {
    wckWMarketingValue = '0';
  }
  return wckWMarketingValue;
};

// WckMMarketingValue(Men) 정보 조회.
export const getCookieWckMMarketingValue = async () => {
  let wckMMarketingValue = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckMMarketingValue = cookie.get('wckMMarketingValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckMMarketingValue = getCookie('wckMMarketingValue') as string;
  }
  if (isEmpty(wckMMarketingValue)) {
    wckMMarketingValue = '0';
  }
  return wckMMarketingValue;
};

export const getCookieWckGenderValue = async (isDesktop?: boolean) => {
  let wckGenderValue = 'All';
  // 데스크탑이고 isDesktop 속성이 있으면 데스크탑 페이지 개발여부
  if (typeof isDesktop === 'boolean' && isDesktop) {
    return 'All';
  }
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckGenderValue = cookie.get('wckGenderValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckGenderValue = getCookie('wckGenderValue') as string;
  }

  // 모바일이고 isDesktop 속성 없으면 데스크탑 페이지 개발여부
  if (isEmpty(wckGenderValue)) {
    wckGenderValue = 'Women';
  }
  return wckGenderValue as 'Women' | 'Men' | 'All';
};

// AS-IS 도메인 정보 조회.
export const getCookieWckDisplayFrontLocation = async () => {
  let wckDisplayFrontLocation = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckDisplayFrontLocation = cookie.get('wckDisplayFrontLocation')
      ?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckDisplayFrontLocation = getCookie('wckDisplayFrontLocation') as string;
  }
  return wckDisplayFrontLocation;
};

// 최근 본 상품코드 쿠키 정보 조회.
export const getCookieWckRP = async () => {
  let wckRP: string[] = [];
  let cookieValue = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookieValue = cookie.get('wckRP')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    cookieValue = getCookie('wckRP') as string;
  }

  if (!isEmpty(cookieValue)) {
    wckRP = String(cookieValue).split(',');
  }
  return wckRP;
};

// 장바구니 상품코드 쿠키 정보 조회.
export const getCookieWckRBP = async () => {
  let wckRBP: string[] = [];
  let cookieValue = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookieValue = cookie.get('wckRBP')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    cookieValue = getCookie('wckRBP') as string;
  }

  if (!isEmpty(cookieValue)) {
    wckRBP = String(cookieValue).split(',');
  }
  return wckRBP;
};

export const getWckDisplaySID = async () => {
  let wckDisplaySID = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckDisplaySID = cookie.get('wckDisplaySID')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckDisplaySID = getCookie('wckDisplaySID') as string;
  }
  return decodeCookie(wckDisplaySID, 'wckDisplaySID')
    ? decodeCookie(wckDisplaySID, 'wckDisplaySID')
    : '';
};

// 최근 검색어 저장. 추후 삭제
export const setRecentResultList = async (
  keywordArr: string[],
  keyword?: string
) => {
  if (isEmpty(keywordArr)) {
    return;
  }

  // 최근 검색어 배열 길이 15개 제한
  if (keywordArr.length > 15) {
    keywordArr.pop();
  }

  // keyword 값 배열에서 삭제
  if (keyword) {
    for (let i = 0; i < keywordArr.length; i++) {
      if (keywordArr[i] === keyword) {
        keywordArr.splice(i, 1);
        i -= 1;
      }
    }
  }

  const date = new Date();
  // 30일 추가 30 * 24 * 60 * 60 * 1000
  date.setTime(date.getTime() + 2592000000);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('recentSearchKeyword', keywordArr.toString(), {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('recentSearchKeyword', keywordArr, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// 최근 검색어 불러오기. 추후 삭제
export const getRecentResultList = async () => {
  let recentSearchKeyword = '0';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    recentSearchKeyword = cookie.get('recentSearchKeyword')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    recentSearchKeyword = getCookie('recentSearchKeyword') as string;
  }
  if (isEmpty(recentSearchKeyword)) {
    recentSearchKeyword = '';
  }
  return recentSearchKeyword;
};

// 최근 검색어 전체 삭제. 추후 삭제
export const clearRecentSearchList = async () => {
  const date = new Date();
  date.setTime(date.getFullYear() - 30);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('recentSearchKeyword', '', {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('recentSearchKeyword', '', {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

export const deleteRecentCookie = async () => {
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.delete('wckRP');
  } else {
    const { deleteCookie } = await import('cookies-next');
    deleteCookie('recentSearchKeyword', {
      path: '/',
      domain: '.wconcept.co.kr',
    });
  }
};

// 최근 검색어 저장.
export const setWckRecentResultList = async (
  keywordArr: string[],
  keyword?: string
) => {
  if (isEmpty(keywordArr)) {
    return;
  }

  // 최근 검색어 배열 길이 15개 제한
  if (keywordArr.length > 15) {
    keywordArr.pop();
  }

  // keyword 값 배열에서 삭제
  if (keyword) {
    for (let i = 0; i < keywordArr.length; i++) {
      if (keywordArr[i] === keyword) {
        keywordArr.splice(i, 1);
        i -= 1;
      }
    }
  }

  const date = new Date();
  // 30일 추가 30 * 24 * 60 * 60 * 1000
  date.setTime(date.getTime() + 2592000000);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set(
      'wckRecentKeyword',
      keywordArr.slice(0, 10)?.reverse()?.join(','),
      {
        path: '/',
        domain: '.wconcept.co.kr',
        expires: date,
      }
    );
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie(
      'wckRecentKeyword',
      keywordArr.slice(0, 10)?.reverse()?.join(','),
      {
        path: '/',
        domain: '.wconcept.co.kr',
        expires: date,
      }
    );
  }
};

// 최근 검색어 불러오기.
export const getWckRecentResultList = async () => {
  let wckRecentKeyword = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckRecentKeyword = cookie.get('wckRecentKeyword')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckRecentKeyword = getCookie('wckRecentKeyword') as string;
  }
  if (isEmpty(wckRecentKeyword)) {
    return '';
  }
  return sanitizeContent(wckRecentKeyword)
    ?.split(',')
    ?.filter(item => !isEmpty(item))
    ?.reverse()
    ?.join();
};

// 최근 검색어 전체 삭제.
export const clearWckRecentSearchList = async () => {
  const date = new Date();
  date.setTime(date.getFullYear() - 30);
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckRecentKeyword', '', {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckRecentKeyword', '', {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// 최근 검색한 검색어 저장
export const setLastKwd = async (keyword: string) => {
  if (isEmpty(keyword)) {
    return;
  }

  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('LastKwd', keyword, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('LastKwd', keyword, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckLiveMktValue(알림톡수신동의여부) 세팅
export const setCookieWckLiveAlarmValue = async (val: string) => {
  if (isEmpty(val)) {
    return;
  }
  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckLiveAlarmValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckLiveAlarmValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wckLiveMktValue(알림톡수신동의여부) 값 조회
export const getCookieWckLiveAlarmValue = async () => {
  let wckLiveAlarmValue = 'N';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckLiveAlarmValue = cookie.get('wckLiveAlarmValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckLiveAlarmValue = getCookie('wckLiveAlarmValue') as string;
  }
  if (isEmpty(wckLiveAlarmValue)) {
    wckLiveAlarmValue = 'N';
  }
  return wckLiveAlarmValue;
};

export const deleteWckIssueSearchKeyword = async () => {
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.delete('wckIssueSearchKeyword');
  } else {
    const { deleteCookie } = await import('cookies-next');
    deleteCookie('wckIssueSearchKeyword', {
      path: '/',
      domain: '.wconcept.co.kr',
    });
  }
};

export const setWckIssueSearchKeyword = async (val: string) => {
  if (isEmpty(val)) {
    deleteWckIssueSearchKeyword();
    return;
  }
  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckIssueSearchKeyword', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckIssueSearchKeyword', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

export const getCustomAdAgreeYn = async () => {
  let customAdAgreeYn = 'N';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    customAdAgreeYn = cookie.get('customAdAgreeYn')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    customAdAgreeYn = getCookie('customAdAgreeYn') as string;
  }
  return customAdAgreeYn;
};

export const getCookieWckDisplayMktSID = async () => {
  let wckDisplayMktSID = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckDisplayMktSID = cookie.get('wckDisplayMktSID')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckDisplayMktSID = getCookie('wckDisplayMktSID') as string;
  }
  return wckDisplayMktSID;
};

// wckDisplaySid setCookie.
export const setWckDisplaySid = async (sid: string) => {
  if (isEmpty(sid)) {
    return;
  }
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckDisplaySID', sid, {
      path: '/',
      domain: '.wconcept.co.kr',
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckDisplaySID', sid, {
      path: '/',
      domain: '.wconcept.co.kr',
    });
  }
};

// WckAuth의 Grade 정보 조회.
/**
 VVIP : 1
 VIP : 2
 PRIME : 3
 BEST : 4
 FRIEND : 5
 WPEOPLE : 6
 LOGIN : 7 (사용안함)
 LOGOFF : 8
 W_SIGNATURE : 11
 W_VIP : 12
 W_BEST : 13
 W_FRIEND : 14
 */
export const getCookieWckAuthGrade = async () => {
  const wckAuthData = await getCookieWckAuthData();
  if (!isEmpty(wckAuthData.userNo)) {
    // 예외처리.
    if (wckAuthData.grade === '0') {
      return '6';
    }
    return wckAuthData.grade;
  }
  return '8';
};

export const getCookieWckGenderValueForMainPage = async () => {
  let wckGenderValue = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckGenderValue = cookie.get('wckGenderValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckGenderValue = getCookie('wckGenderValue') as string;
  }
  return wckGenderValue;
};

export const getCooikeShareFacebook = async () => {
  let shareFacebook = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    shareFacebook = cookie.get('shareFacebook')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    shareFacebook = getCookie('shareFacebook') as string;
  }
  return shareFacebook;
};

export const setCookieShareFacebook = async (val: string) => {
  if (isEmpty(val)) {
    deleteWckIssueSearchKeyword();
    return;
  }
  const todayDate = new Date();
  todayDate.setDate(todayDate.getDate() + 1);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('shareFacebook', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: todayDate,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('shareFacebook', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: todayDate,
    });
  }
};

export const getCooikeShareKakaotalk = async () => {
  let shareKakaotalk = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    shareKakaotalk = cookie.get('shareKakaotalk')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    shareKakaotalk = getCookie('shareKakaotalk') as string;
  }
  return shareKakaotalk;
};

export const setCookieShareKakaotalk = async (val: string) => {
  if (isEmpty(val)) {
    deleteWckIssueSearchKeyword();
    return;
  }
  const todayDate = new Date();
  todayDate.setDate(todayDate.getDate() + 1);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('shareKakaotalk', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: todayDate,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('shareKakaotalk', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: todayDate,
    });
  }
};

// wckMktAdValue(알림톡수신동의여부) 값 조회
export const getCookieWckMktAdValue = async () => {
  let wckMktAdValue = 'N';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckMktAdValue = cookie.get('wckMktAdValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckMktAdValue = getCookie('wckMktAdValue') as string;
  }
  if (isEmpty(wckMktAdValue)) {
    wckMktAdValue = 'N';
  }
  return wckMktAdValue;
};

// wckMktValue(알림톡수신동의여부) 세팅
export const setCookieWckMKtAdValue = async (val: 'Y' | 'N') => {
  if (isEmpty(val)) {
    return;
  }
  const date = new Date();
  // 24시간 저장
  date.setHours(date.getHours() + 24);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckMktAdValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckMktAdValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

export const deleteCookieWckRPImage = async () => {
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.delete('wckRPImage');
  } else {
    const { deleteCookie } = await import('cookies-next');
    deleteCookie('wckRPImage', {
      path: '/',
      domain: '.wconcept.co.kr',
    });
  }
};

export const getCookieWckRPImage = async () => {
  let wckRPImage = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckRPImage = cookie.get('wckRPImage')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckRPImage = getCookie('wckRPImage') as string;
  }
  return wckRPImage;
};

// wckMyHeartTtValue (마이하트 Tooltip) 값 조회
export const getCookieWckMyHeartTtValue = async () => {
  let wckMyHeartTtValue = 'N';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckMyHeartTtValue = cookie.get('wckMyHeartTtValue')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckMyHeartTtValue = getCookie('wckMyHeartTtValue') as string;
  }
  if (isEmpty(wckMyHeartTtValue)) {
    wckMyHeartTtValue = 'N';
  }
  return wckMyHeartTtValue;
};

// wckMyHeartTtValue (마이하트 Tooltip) 세팅
export const setCookieWckMyHeartTtValue = async (val: 'Y' | 'N') => {
  if (isEmpty(val)) {
    return;
  }
  const date = new Date();
  // 10년 저장
  date.setFullYear(date.getFullYear() + 10);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckMyHeartTtValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckMyHeartTtValue', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// wck_designer_tooltip (브랜드 탭 Tooltip) 값 조회
export const getCookieWckDesignerTooltip = async () => {
  let wckDesignerTooltip = 'N';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckDesignerTooltip = cookie.get('wck_designer_tooltip')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckDesignerTooltip = getCookie('wck_designer_tooltip') as string;
  }
  if (isEmpty(wckDesignerTooltip)) {
    wckDesignerTooltip = 'N';
  }
  return wckDesignerTooltip;
};

// wck_designer_tooltip (브랜드 탭 Tooltip) 세팅
export const setCookieWckDesignerTooltip = async (val: 'Y' | 'N') => {
  if (isEmpty(val)) {
    return;
  }
  const date = new Date();
  // 10년 저장
  date.setFullYear(date.getFullYear() + 10);

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wck_designer_tooltip', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wck_designer_tooltip', val, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires: date,
    });
  }
};

// 스타일클립 프로필 sqeNo
export const getCookieWckStyleClipNo = async () => {
  let wckStyleClipNo = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckStyleClipNo = cookie.get('wckStyleClipNo')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckStyleClipNo = getCookie('wckStyleClipNo') as string;
  }
  return wckStyleClipNo?.toString();
};

export const setCookieWckStyleClipNo = async ({
  value,
  isAuthLogin,
  isMobile,
}: {
  value: string;
  isAuthLogin: boolean;
  isMobile: boolean;
}) => {
  if (isEmpty(value)) {
    return;
  }
  const date = new Date();
  let expires;
  // 1년 저장
  if (isAuthLogin && isMobile) {
    date.setFullYear(date.getFullYear() + 1);
    expires = date;
  }

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckStyleClipNo', value, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckStyleClipNo', value, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires,
    });
  }
};

export const getCookieWckStyleClipCursor = async () => {
  let wckStyleClipCursor = '';
  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    wckStyleClipCursor = cookie.get('wckStyleClipCursor')?.value as string;
  } else {
    const { getCookie } = await import('cookies-next');
    wckStyleClipCursor = getCookie('wckStyleClipCursor') as string;
  }
  return wckStyleClipCursor?.toString();
};

export const setCookieWckStyleClipCursor = async ({
  value,
  isAuthLogin,
}: {
  value: string;
  isAuthLogin: boolean;
}) => {
  if (isEmpty(value)) {
    return;
  }
  const date = new Date();
  let expires;
  // 1년 저장
  if (isAuthLogin) {
    date.setFullYear(date.getFullYear() + 1);
    expires = date;
  }

  if (isSsr()) {
    const { cookies } = await import('next/headers');
    const cookie = await cookies();
    cookie.set('wckStyleClipCursor', value, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires,
    });
  } else {
    const { setCookie } = await import('cookies-next');
    setCookie('wckStyleClipCursor', value, {
      path: '/',
      domain: '.wconcept.co.kr',
      expires,
    });
  }
};

export const trimExistingCookieValue = async (name: string) => {
  // 앞뒤 공백과 %20 제거
  const cleanValue = (value: string): string =>
    value.trim().replace(/^(%20)+|(%20)+$/g, '');

  console.log('cleanValue==', name);
  if (isSsr()) {
    // 서버 사이드
    try {
      const { cookies } = await import('next/headers');
      const cookie = await cookies();
      const existingCookie = cookie.get(name);
      console.log('ready=ssr=', existingCookie);
      if (existingCookie?.value) {
        const cleanedValue = cleanValue(existingCookie.value);
        if (cleanedValue !== existingCookie.value) {
          console.log('result=ssr=', cleanedValue);
          cookie.set(name, cleanedValue);
        }
      }
    } catch (error) {
      console.error(`Error cleaning cookie ${name} on server:`, error);
    }
  } else {
    // 클라이언트 사이드
    try {
      const { getCookie } = await import('cookies-next');
      const currentValue = getCookie(name) as string;

      // 값이 있고 실제로 공백이나 %20이 제거되었을 때만 진행
      console.log('ready=isr=', currentValue);
      if (currentValue) {
        const cleanedValue = cleanValue(currentValue);

        if (cleanedValue !== currentValue) {
          // 기존 쿠키의 모든 속성 파싱
          const cookies = document.cookie.split(';');
          const targetCookie = cookies.find(cookie =>
            cookie.trim().startsWith(`${name}=`)
          );

          if (targetCookie) {
            // expires만 추출
            const cookieString = targetCookie.trim();
            const expiresMatch = cookieString.match(/expires=([^;]+)/i);

            // expires만 유지하면서 값 변경
            let newCookie = `${name}=${encodeURIComponent(
              cleanedValue
            )}; path=/; domain=.wconcept.co.kr`;
            if (expiresMatch) {
              newCookie += `; expires=${expiresMatch[1]}`;
            }
            console.log('result=isr=', newCookie);
            document.cookie = newCookie;
          }
        }
      }
    } catch (error) {
      console.error(`Error cleaning cookie ${name} on client:`, error);
    }
  }
};
